/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import { Box, FlexContainer, Input, theme } from "@nordcloud/gnui";
import { ReactTable, SortDirection, SortSelector } from "~/components";
import { useQueryState } from "~/hooks";
import { isEmpty } from "~/tools";
import { SELECT_OPTIONS, SortFilter } from "~/views/businessContexts/constants";
import { List } from "../List";
import { SearchParams, SpendTreeItem } from "../types";
import { OrganizationUnitChildTableColumns } from "./OrganizationUnitChildTableColumns";

type Props = {
  items: SpendTreeItem[];
  unitId: string;
};

export function OrganizationUnitStructure({ items, unitId }: Props) {
  const {
    set,
    removeFields,
    state: {
      search = "",
      sortField = SortFilter.UnitName,
      sortDirection = SortDirection.Ascending,
    },
  } = useQueryState<SearchParams>();

  const columns = useMemo(
    () => OrganizationUnitChildTableColumns(unitId),
    [unitId]
  );

  const handleSearch = (value: string) => {
    if (isEmpty(value)) {
      removeFields(["search"]);
    } else {
      set("search")(value);
    }
  };

  return (
    <List
      isEmpty={isEmpty(items) && isEmpty(search.toString())}
      renderMessageContent={{
        emptyMessage: "The Organizational Unit does not have nested units",
      }}
    >
      <Box boxStyle="lightGrey" spacing="spacing04">
        <FlexContainer
          justifyContent="flex-start"
          alignItems="center"
          columnGap={theme.spacing.spacing04}
        >
          <SortSelector
            options={SELECT_OPTIONS}
            value={sortField}
            direction={sortDirection}
            onSelect={(filter) => set("sortField")(filter)}
            onSortChange={(value) => set("sortDirection")(value)}
          />
          <div css={{ flex: 1 }}>
            <Input
              name="search-input"
              placeholder="Search"
              type="search"
              value={search}
              loading={false}
              onChange={(event) => handleSearch(event.target.value)}
            />
          </div>
        </FlexContainer>
      </Box>
      <ReactTable columns={columns} data={items} />
    </List>
  );
}
